import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "@/components/ui/accordion";
import { Video } from "lucide-react";
import { Reveal } from "./Reveal";
import { ZOOM_SCHEDULER_URL } from "@/constants/options";

const FAQS = [
    {
        q: "What exactly does the $399 cover?",
        a: "It's a one-time membership fee that places you in our 2026 RAV4 allocation pool and reserves an allocation in your name. It is not a down payment on the vehicle — but here's the kicker: we guarantee your vehicle comes in below Total Suggested Retail Price even with our fee included, so the membership effectively pays for itself.",
    },
    {
        q: "Is the membership fee refundable?",
        a: "Yes — completely. Your allocation is guaranteed within 90 days of your payment. If we ever miss that window, we provide a full and complete refund of your $399 — no questions, no deductions.",
    },
    {
        q: "How do I pay the membership fee?",
        a: "Securely by card, right after you reserve — checkout is handled by Stripe and every major card is accepted. Your membership is confirmed the moment payment clears.",
    },
    {
        q: "Will I pay a dealer markup?",
        a: "No — quite the opposite. We guarantee your vehicle is acquired below Total Suggested Retail Price, and that stays true even after adding our $399 fee. No market adjustments, no bidding wars: below sticker, fee included.",
    },
    {
        q: "Which trims and colors can I choose?",
        a: "All six 2026 RAV4 trims — LE, SE, XLE Premium, Woodland, XSE, and Limited, gas and hybrid — and every factory color. You tell us your preference when you join, and we allocate to match as closely as possible.",
    },
    {
        q: "What is the difference between FIO and DIO?",
        a: "Factory Installed Options (FIO) are fitted on the assembly line and hold their value. Dealer, Distributor, and Local Installed Options (DIO) are bolted on after the fact — protection packages, pinstripes, marked-up accessories — and lose value immediately. Through bulk, fleet, and consortium buying, every member allocation ships FIO-only: lower purchase price, no low-residual add-ons, higher resale value.",
    },
    {
        q: "How do I take delivery of my vehicle?",
        a: "We notify you by email and text the moment your vehicle is on the dealership lot — we allocate based on your location, matching you with dealers in your region to minimize logistics costs. You can pick up in person (our recommendation) or have it delivered by a fully insured carrier. All purchasing and financing paperwork is completed ahead of pick-up, so the process is quick, easy, and seamless.",
    },
    {
        q: "How long until I get my RAV4?",
        a: "Allocation is guaranteed within 90 days of your membership payment — most members are confirmed far sooner. If we ever miss that window, you receive a full and complete refund. Delivery timing then depends on your trim and production schedule, but members are sequenced ahead of public waitlists.",
    },
];

export const Faq = () => (
    <section id="faq" data-testid="faq-section" className="border-t border-white/10 bg-[#080808] py-28 sm:py-36">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-5">
                <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-[#2563EB]">
                    <span className="h-2 w-2 rotate-45 bg-[#2563EB]" aria-hidden="true" />
                    Questions
                </p>
                <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-tighter sm:text-5xl">
                    Straight answers, no fine print.
                </h2>
                <p className="mt-6 max-w-sm text-base leading-relaxed text-zinc-400 md:text-lg">
                    Everything members ask before joining. Still unsure? Write to{" "}
                    <span className="text-white">membership@rav4buyersclub.com</span>.
                </p>
                <p
                    data-testid="real-live-text"
                    className="mt-8 max-w-sm font-display text-lg font-bold uppercase leading-snug tracking-tight text-white"
                >
                    We are real. We are live.{" "}
                    <span className="text-[#2563EB]">
                        We take immediate Zoom or phone calls when available.
                    </span>
                </p>
                <a
                    data-testid="faq-book-call-button"
                    href={ZOOM_SCHEDULER_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex items-center gap-3 bg-[#2563EB] px-8 py-4 text-sm font-bold uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                >
                    <Video className="h-4 w-4" aria-hidden="true" />
                    Set up a video call
                </a>
            </Reveal>
            <Reveal className="lg:col-span-7" delay={0.15}>
                <Accordion type="single" collapsible data-testid="faq-accordion">
                    {FAQS.map((item, i) => (
                        <AccordionItem key={i} value={`faq-${i}`} className="border-white/10">
                            <AccordionTrigger
                                data-testid={`faq-question-${i}`}
                                className="py-6 text-left font-display text-lg font-bold uppercase tracking-tight hover:text-[#2563EB] hover:no-underline sm:text-xl"
                            >
                                {item.q}
                            </AccordionTrigger>
                            <AccordionContent
                                data-testid={`faq-answer-${i}`}
                                className="pb-6 text-sm leading-relaxed text-zinc-400 sm:text-base"
                            >
                                {item.a}
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </Reveal>
        </div>
    </section>
);
